import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { Observable, throwError } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import { AuthService } from '../../auth/auth.service'
import { ActiveDeviceService } from '../services/active-device.service'

export const refreshTokenInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService)
  const activeDeviceService = inject(ActiveDeviceService)
  const router = inject(Router)
  const isRefreshRequest = /\/auth\/refresh-tokens(?:\?|$)/.test(req.url)
  const isLoginRequest = /\/auth\/login(?:\/|\?|$)/.test(req.url)

  // Re-stamp a replayed request with the fresh access token. It must keep
  // the ORIGINAL body: this only ever runs on replays of the caller's own
  // request (the refresh call itself goes through authService), so
  // overwriting `body` here silently discarded whatever the user was
  // saving.
  const addToken = (req: HttpRequest<any>, token: string) => {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  // Idempotent by construction: once logout() has cleared storage there is
  // nothing left to clear and concurrent failures become no-ops — no
  // module-level hasLoggedOut flag to leak state across logins. Gated on the
  // refresh token too, not just isLoggedIn(): that also needs the cached
  // `user`, and a session stored without one kept its dead refresh token and
  // re-sent it on every 439.
  const forceLogout = () => {
    if (!authService.isLoggedIn() && !authService.getRefreshToken()) return
    authService.logout()
    // Save the current URL for redirecting after login
    const returnUrl = router.url
    router.navigate(['/login'], { queryParams: { returnUrl } })
  }

  // All concurrent callers share ONE refresh (single-flight lives in
  // AuthService, shared with the route guards), then each replays its own
  // request and receives its own response — the old module-level pending
  // queue replayed requests with a bare .subscribe() and threw the
  // responses away, leaving the original callers hanging forever. The
  // rejected bearer goes along so a 439 that arrives after the session was
  // already refreshed replays with the new token instead of rotating again.
  const refreshAndReplay = (req: HttpRequest<any>, next: HttpHandlerFn, error: HttpErrorResponse) => {
    if (!authService.getRefreshToken()) return throwError(() => error)
    const rejectedToken = req.headers.get('Authorization')?.replace(/^Bearer\s+/i, '') || undefined
    return authService.refreshSession(rejectedToken).pipe(
      switchMap((res: any) => {
        const accessToken = res?.data?.tokens?.accessToken?.token
        if (!accessToken) {
          forceLogout()
          return throwError(() => new Error('Session refresh returned no access token'))
        }
        return next(addToken(req, accessToken))
      }),
      catchError(err => {
        // Only a definitive rejection of the session logs out; transient
        // refresh failures (offline, 5xx) surface to the caller and the
        // session stays for a later retry.
        if (err?.status === 401) forceLogout()
        return throwError(() => err)
      }),
    )
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Login failures belong to the login form. Refreshing here can replace
      // the actual error with a 400 from a missing refresh-token payload.
      // Refresh requests must also never recursively refresh themselves.
      if (isLoginRequest || isRefreshRequest) {
        if (isRefreshRequest && error.status === 401) forceLogout()
        return throwError(() => error)
      }
      if (error.status === 402) {
        return throwError(() => error)
      } else if (error.status === 439) {
        // 403 deliberately NOT handled here. It is an authorization
        // decision (ACCESS_DENIED / ACCOUNT_READONLY), not an expired
        // token — refreshing cannot change the outcome. Routing it
        // through the refresh path burned a refresh per attempt and
        // replaced the real, already-translated reason with whatever
        // generic message the caller falls back to. Let it through to
        // errorTranslation.interceptor.
        return refreshAndReplay(req, next, error)
      } else if (error.status === 423) {
        const code = error.error?._originalCode || error.error?.message
        // ANOTHER_DEVICE_ACTIVE = the session is valid but another device
        // holds the single-writer lease. Park the write and offer a take-over.
        if (code === 'ANOTHER_DEVICE_ACTIVE') {
          return activeDeviceService.holdRejectedWrite(req, next, error)
        }
        // USER_LOGGED_IN_FROM_ANOTHER_DEVICE: the active-token cache entry
        // mismatched — a rotation overlap or a Redis flush, NOT proof the
        // session is dead. A refresh re-establishes the entry; only a 401
        // from the refresh endpoint (handled inside refreshAndReplay) means
        // the session is actually gone. Logging out here directly is what
        // used to mass-logout users on every cache loss.
        return refreshAndReplay(req, next, error)
      } else {
        return throwError(() => error)
      }
    }),
  )
}
